import { makeStyles } from '@mui/styles';
import { neutral7 } from 'apollo-react/colors';
import Button from 'apollo-react/components/Button';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import Table from 'apollo-react/components/Table';
import TextField from 'apollo-react/components/TextField';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import SearchIcon from 'apollo-react-icons/Search';
import Trash from 'apollo-react-icons/Trash';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useFormSelector, useTableLastRowVisibilityEffect } from '../../../../../custom-hooks';
import { getUITA } from '../../../../../utils/commonUtils';
import {
  CLOSE_PARENTHESIS,
  ENTITY_MODEL_EDITOR,
  EXPRESSION_EDITOR,
  LOGICAL_OPERATORS,
  OPEN_PARENTHESIS,
  SQL_DICTIONARY,
} from '../../../../constants/editor.constants';
import {
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
} from '../../../../constants/form-manager.constants';
import { getEntityDefinition, getTranslations } from '../../../../selectors/common.selectors';
import { getDataSourceFieldList, normalizeConditionsRows } from '../../../../utils/editor.utils';
import TablePropertiesModal from '../TablePropertiesModal';

const useStyles = makeStyles({
  root: {
    overflow: 'hidden',
    '& [class*="Table-tableWrapper"]': {
      overflow: 'hidden',
    },
    '& [class*="MuiToolbar-root"]': {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    '& [class*="MuiToolbar-root"] div': {
      paddingRight: 20,
    },
    '& [class*="MuiTableHead-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="MuiTable-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="InternalTableBody-editMode"]': {
      backgroundColor: 'white',
      borderTop: 'none !important',
      borderBottom: 'none !important',
    },
    '& [class*="middleAlign"]': {
      height: '60px !important',
    },

    '& [class*="EmptyRow-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
      minHeight: '239px',
    },
  },
});

const ActionCell = ({ row }) => {
  const { Id, onDelete } = row;
  return (
    <div>
      <IconButton ui-ta={getUITA('ActionCell', 'DeleteBtn')} onClick={() => onDelete(Id)}>
        <Trash />
      </IconButton>
    </div>
  );
};

const fieldStyles = {
  style: {
    margin: 0,
  },
};

const DataSourceValuesList = ({ row }) => {
  const columnList =
    row.name === FORM_MODEL_ATTRIBUTES.FILTER
      ? row.leftValueFields
      : row.dataSourceFilterColumns || [];
  const [value, setValue] = useState(row.LeftValue);

  const onChangeHandler = (event) => {
    setValue(event.target.value);
    onChangeFunction(event.target.value);
  };

  const onChangeFunction = (value) => {
    row.editRow(row.Id, 'LeftValue', value);
  };

  return (
    <div style={{ width: 'auto' }}>
      <Select
        size="small"
        fullWidth
        canDeselect={false}
        onBlur={(e) => {
          onChangeFunction(e.target.value);
        }}
        value={value}
        onChange={onChangeHandler}
        {...fieldStyles}
        ui-ta={getUITA('LeftValue', 'dataSource', 'dropDown')}
      >
        {columnList.map(({ label, value }) => (
          <MenuItem key={value} value={value}>
            {row.isGridOrRepeatingPanelControl ? label : `${label} (${value})`}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
};

const EditableCellOpenEntity = ({ row, column: { accessor: key } }) => {
  const { handleOpenTablePropertiesModal } = row;
  const [value, setValue] = useState(row[key]);

  useEffect(() => {
    setValue(row[key]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row[key]]);

  return row.editMode ? (
    <Tooltip
      extraLabels={[
        { title: '', subtitle: '' },
        { title: '', subtitle: row[key] },
      ]}
      placement="bottom"
      variant="light"
    >
      {row.isDataSource && key === 'LeftValue' ? (
        <DataSourceValuesList row={row} />
      ) : (
        <TextField
          size="small"
          fullWidth
          value={value ?? ''}
          icon={<SearchIcon color="primary" />}
          iconProps={{
            onClick: () => {
              handleOpenTablePropertiesModal(key, row);
            },
            size: 'small',
            'ui-ta': getUITA('Modal open button'),
          }}
          onChange={({ target }) => {
            if (row.canBeColumn && key === 'LeftValue') {
              return;
            }
            setValue(target.value);
          }}
          onBlur={() => {
            if (row.canBeColumn && key === 'LeftValue') {
              return;
            }
            row.editRow(row.Id, key, value);
          }}
          {...fieldStyles}
          ui-ta={getUITA(key === 'LeftValue' ? 'LeftValue' : 'RightValue', 'select')}
        />
      )}
    </Tooltip>
  ) : (
    row[key]
  );
};

const makeEditableSelectCell = (options) => ({ row, column: { accessor: key } }) =>
  row.editMode ? (
    <div style={{ width: 'auto' }}>
      <Select
        size="small"
        fullWidth
        canDeselect={false}
        value={row[key]}
        onChange={(e) => {
          row.editRow(row.Id, key, e.target.value);
        }}
        {...fieldStyles}
        ui-ta={getUITA(key, 'dropDown')}
      >
        {Object.entries(options).map(([key, value]) => {
          return (
            <MenuItem key={key} value={key}>
              {value}
            </MenuItem>
          );
        })}
      </Select>
    </div>
  ) : (
    row[key]
  );

const readOnlyColumn = (options) => ({ row, column: { accessor: key } }) => {
  const providedValue = () => {
    // provide value to column by default
    let value = row[key] ?? '';

    // condition for providing correct value in rule section mapping modal
    if (row[key] && key === 'DummyFrom') {
      value = `${row[key]} (${row.From})`;
    }

    // condition for providing translated value in rule section condition modal
    if (row[key] && key === 'OpenGroup') {
      value = '(';
    } else if (row[key] && key === 'CloseGroup') {
      value = ')';
    } else if (options) {
      value = options[row[key]] ?? '';
    }

    return value;
  };
  return (
    <Typography variant="body2" style={{ color: neutral7 }}>
      {providedValue()}
    </Typography>
  );
};

const columns = [
  {
    header: '(',
    accessor: 'OpenGroup',
    customCell: makeEditableSelectCell(OPEN_PARENTHESIS),
  },
  {
    header: 'LeftValue',
    accessor: 'LeftValue',
    customCell: EditableCellOpenEntity,
  },
  {
    header: 'SQL Operator',
    accessor: 'SqlOperator',
    customCell: makeEditableSelectCell(SQL_DICTIONARY),
  },
  {
    header: 'RightValue',
    accessor: 'RightValue',
    customCell: EditableCellOpenEntity,
  },
  {
    header: ')',
    accessor: 'CloseGroup',
    customCell: makeEditableSelectCell(CLOSE_PARENTHESIS),
  },
  {
    header: 'Operator',
    accessor: 'RelationalOperator',
    customCell: makeEditableSelectCell(LOGICAL_OPERATORS),
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

const readOnlyColumns = [
  {
    header: '(',
    accessor: 'OpenGroup',
    customCell: readOnlyColumn(OPEN_PARENTHESIS),
    width: 80,
  },
  {
    header: 'LeftValue',
    accessor: 'LeftValue',
    customCell: readOnlyColumn(),
  },
  {
    header: 'SQL Operator',
    accessor: 'SqlOperator',
    customCell: readOnlyColumn(SQL_DICTIONARY),
  },
  {
    header: 'RightValue',
    accessor: 'RightValue',
    customCell: readOnlyColumn(),
  },
  {
    header: ')',
    accessor: 'CloseGroup',
    customCell: readOnlyColumn(CLOSE_PARENTHESIS),
    width: 80,
  },
  {
    header: 'Operator',
    accessor: 'RelationalOperator',
    customCell: readOnlyColumn(LOGICAL_OPERATORS),
  },
];

const ConditionModal = ({
  name,
  ruleId,
  isDataSource,
  sourceType,
  sourceIdentifier,
  currentControlModel,
  tempFilterConditions,
  setCurrentControlModel,
  dataSourceFilterColumns,
  isReadOnly,
  controlType,
  selectValuesFromEntity,
}) => {
  const classes = useStyles();

  const controllerUpdateValue = currentControlModel[name];
  const tempControllerUpdateValue = controllerUpdateValue?.rules;
  const entityDefinition = useFormSelector(getEntityDefinition);
  const translations = useFormSelector(getTranslations);

  const propValue = ruleId
    ? tempControllerUpdateValue
      ? tempControllerUpdateValue.find((item) => item.id === ruleId)
      : []
    : controllerUpdateValue;

  const Conditions = name === 'dataSourceKey' ? tempFilterConditions : propValue?.Conditions || [];

  const canBeColumn = name === 'targetFilter';

  const newRows =
    Conditions && Conditions.length
      ? Conditions.map(normalizeConditionsRows(entityDefinition, translations, canBeColumn))
      : [];

  const [rows, setRows] = useState(newRows);
  const [leftValueFields, setFieldList] = useState([]);
  const [isTablePropertiesModalOpen, setIsTablePropertiesModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  const tableRef = useRef();

  useTableLastRowVisibilityEffect({ tableRef, rows });

  const isGridOrRepeatingPanelControl =
    controlType === SS_CONTROL_TYPE.GRID || controlType === SS_CONTROL_TYPE.REPEATING_PANEL;

  useEffect(() => {
    if (name === FORM_MODEL_ATTRIBUTES.FILTER) {
      if ([SS_CONTROL_TYPE.ZOOM, SS_CONTROL_TYPE.SAVED_SEARCH].includes(controlType)) {
        getDataSourceFieldList({
          dataLookupType: sourceType,
          dataObject: sourceIdentifier,
        }).then((res) => {
          setFieldList(res);
        });
      }
      if (isGridOrRepeatingPanelControl) {
        setFieldList(selectValuesFromEntity);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateLocalState = (newRows) => {
    //Condition
    if (!ruleId) {
      let newValue = '';
      if (newRows && newRows.length > 0) {
        newValue = {
          Conditions: newRows.map((row) => {
            const isLeftTypeColumn =
              canBeColumn && (row.LeftType === 'column' || _.isNil(row.LeftType));

            return {
              ...row,
              OpenGroup: row.OpenGroup === '(' ? true : false,
              CloseGroup: row.CloseGroup === ')' ? true : false,
              LeftType: isDataSource ? 'datasource' : isLeftTypeColumn ? 'column' : 'formula',
              RightType: 'formula',
            };
          }),
        };
      }

      setCurrentControlModel((prev) => ({ ...prev, [name]: newValue }));
    }

    //Condition from rules
    if (ruleId) {
      const elementsIndex = controllerUpdateValue.rules.findIndex((element) =>
        element.id.includes(ruleId)
      );
      const newValue = [...controllerUpdateValue.rules];
      newValue[elementsIndex] = {
        ...newValue[elementsIndex],
        Conditions: newRows.map((row) => ({
          ...row,
          OpenGroup: row.OpenGroup === '(' ? true : false,
          CloseGroup: row.CloseGroup === ')' ? true : false,
          LeftType: 'formula',
          RightType: 'formula',
        })),
      };

      const newRules = newValue.map((item) => {
        const newItem = Object.assign({}, item);
        if (item && item.Conditions) {
          newItem.description = item.Conditions.map(
            (i) =>
              `${i.OpenGroup ? '(' : ''} ${i.LeftValue} ${i.SqlOperator &&
                SQL_DICTIONARY[i.SqlOperator]} ${i.RightValue} ${i.CloseGroup ? ')' : ''} ${
                i.RelationalOperator
              } `
          ).join('');

          if (!newItem.description) {
            newItem.description = 'True';
          }
        }
        return newItem;
      });

      setCurrentControlModel((prev) => ({ ...prev, [name]: { rules: newRules } }));
    }

    setRows(newRows);
  };

  const onDelete = (Id) => {
    const newRows = rows.filter((row) => row.Id !== Id);
    updateLocalState(newRows);
  };

  const editRow = (Id, key, value) => {
    const currentRows = rows.map((row) => (row.Id === Id ? { ...row, [key]: value } : row));
    setRows(currentRows);
    updateLocalState(currentRows);
  };

  const addRow = () => {
    const rowId = uuidv4();
    const newCondition = {
      Id: rowId,
      CloseGroup: '',
      OpenGroup: '',
      LeftValue: '',
      RelationalOperator: 'AND',
      RightValue: '',
      SqlOperator: '',
    };
    const newRows = [...rows, newCondition];
    updateLocalState(newRows);
  };

  const CustomHeader = () => (
    <div>
      <Button variant="text" onMouseDown={addRow} ui-ta={getUITA('ModalButton', 'Add Condition')}>
        {'Add Condition'}
      </Button>
    </div>
  );
  const currentKey = useRef('');

  const handleOpenTablePropertiesModal = (key, row) => {
    currentKey.current = key;
    setIsTablePropertiesModalOpen(true);
    setSelectedRow({ ...row, currentKey: key });
  };

  const getModalType = (row) =>
    row.currentKeyTemp === 'LeftValue' && row.canBeColumn ? ENTITY_MODEL_EDITOR : EXPRESSION_EDITOR;

  return (
    <>
      <div className={classes.root} ui-ta={getUITA('Modal Conditions')} ref={tableRef}>
        <Table
          ui-ta={getUITA('Conditions Details Table')}
          title="Conditions Details"
          columns={isReadOnly ? readOnlyColumns : columns}
          rows={rows.map((row) => ({
            ...row,
            onDelete,
            editRow,
            editMode: !isReadOnly,
            key: row.Id,
            classes,
            isDataSource,
            dataSourceFilterColumns,
            canBeColumn,
            name,
            leftValueFields,
            isGridOrRepeatingPanelControl,
            handleOpenTablePropertiesModal,
          }))}
          initialSortOrder="asc"
          hidePagination
          CustomHeader={!isReadOnly && CustomHeader}
        />
      </div>
      {isTablePropertiesModalOpen && (
        <TablePropertiesModal
          type={getModalType(selectedRow)}
          row={selectedRow}
          handleUpdate={(id, key, value) => {
            editRow(id, key, value);
          }}
          onClose={() => {
            setIsTablePropertiesModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default ConditionModal;
